import { startOfDay } from "date-fns";
import { z } from "zod";

export const HotelListSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.number(),
  type: z.enum(["Hotel", "City"]),
  city_code: z.number(),
  country_code: z.string(),
  star_category: z.number().optional(),
  country_name: z.string(),
  rank: z.number().optional(),
});

export const RoomSchema = z
  .object({
    adults: z.number().min(1, "At least 1 adult"),
    children: z.number().min(0),
    infants: z.number().min(0),
    children_ages: z.array(z.number().min(2).max(17)).optional(),
  })
  .refine(
    (data) => {
      if (data.children > 0) {
        return data.children_ages?.length === data.children;
      }
      return true;
    },
    {
      message: "All children ages must be specified",
      path: ["children_ages"],
    }
  );

const dateRangeSchema = z
  .object({
    from: z
      .date({ message: "Check-in date is required" })
      .or(z.string({ message: "Check-in date is required" })),
    to: z
      .date({ message: "Check-out date is required" })
      .or(z.string({ message: "Check-out date is required" })),
  })
  .refine(
    (d) => {
      const from = new Date(d.from);
      return !Number.isNaN(from.getTime()) && from >= startOfDay(new Date());
    },
    { message: "Check-in date cannot be in the past", path: ["from"] },
  )
  .refine(
    (d) => {
      const from = new Date(d.from);
      const to = new Date(d.to);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()))
        return true; // let the required-check handle it
      return to > from;
    },
    { message: "Check-out date must be after check-in", path: ["to"] },
  );

export const hotelSearchSchema = z.object({
  location: z.custom<z.infer<typeof HotelListSchema>>(
    (value) => HotelListSchema.safeParse(value).success,
    {
      message: "Destination is required",
    },
  ),
  date: dateRangeSchema,
  rooms: z.array(RoomSchema).min(1),
});
